import { useState } from "react";
import { Input } from "@/components/ui/input";
import { PencilSimple, Plus, X, Check } from "@phosphor-icons/react";

/*
  Iter 85.62 · StrategicList
  ──────────────────────────
  Renders a canonical string[] field (Objectives, Key Risks, …) as an
  individually-scannable list rather than a single multi-line textarea.
  Every row is:
    • Clickable to enter inline-edit mode
    • Deletable via the X hover control (with confirm-by-blur)
    • Anchored to the same canonical field on save — no parallel object
      model. `onSave(nextArray)` writes the whole array back so the
      caller uses the exact same PUT shape as before.
  Add-row surface at the bottom; Enter commits, blur commits, Escape
  discards. Empty state shows a restrained placeholder line.

  Props:
    items      : string[] — current canonical array (may be empty)
    onSave     : (nextArray) => void — commits the mutation
    accent     : "objective" | "risk" — colors the left rule
    testidRoot : prefix for row testids (e.g. "engagement-objective")
    addLabel   : label on the add-row surface
    empty      : text shown when items is empty
*/

const ACCENT = {
  objective: "border-primary/60",
  risk: "border-destructive/70",
};

export default function StrategicList({
  items = [],
  onSave,
  accent = "objective",
  testidRoot,
  addLabel = "Add",
  empty = "Nothing captured yet",
}) {
  const [editIdx, setEditIdx] = useState(null);
  const [draft, setDraft] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState("");
  const rule = ACCENT[accent] || ACCENT.objective;

  const commitEdit = () => {
    if (editIdx == null) return;
    const val = draft.trim();
    const cur = items[editIdx] || "";
    if (val === cur.trim()) { setEditIdx(null); return; }
    const next = [...items];
    if (val === "") next.splice(editIdx, 1);
    else next[editIdx] = val;
    onSave(next);
    setEditIdx(null);
  };

  const removeIdx = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    onSave(next);
  };

  const commitAdd = () => {
    const val = addDraft.trim();
    if (!val) { setAddOpen(false); setAddDraft(""); return; }
    // Case-insensitive dedup — never surface the same objective twice.
    const low = new Set(items.map((s) => (s || "").toLowerCase().trim()));
    if (low.has(val.toLowerCase())) { setAddOpen(false); setAddDraft(""); return; }
    onSave([...items, val]);
    setAddOpen(false);
    setAddDraft("");
  };

  return (
    <ul className="space-y-1" data-testid={`${testidRoot}-list`}>
      {items.length === 0 && !addOpen && (
        <li className="text-xs italic text-muted-foreground py-2">{empty}</li>
      )}
      {items.map((it, i) => (
        <li
          key={`${testidRoot}-${i}`}
          data-testid={`${testidRoot}-row-${i}`}
          className={`group flex items-start gap-2 border-l-2 ${rule} pl-3 py-1.5 hover:bg-secondary/30 transition-colors`}
        >
          {editIdx === i ? (
            <Input
              autoFocus
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onBlur={commitEdit}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") commitEdit();
                if (ev.key === "Escape") { setEditIdx(null); setDraft(""); }
              }}
              data-testid={`${testidRoot}-input-${i}`}
              className="flex-1 rounded-none bg-transparent border-0 p-0 h-auto font-serif text-sm leading-relaxed focus-visible:ring-0"
            />
          ) : (
            <button
              type="button"
              onClick={() => { setEditIdx(i); setDraft(it || ""); }}
              data-testid={`${testidRoot}-edit-${i}`}
              className="flex-1 text-left font-serif text-sm leading-relaxed cursor-text hover:text-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              aria-label={`Edit ${it}`}
            >
              {it}
            </button>
          )}
          {editIdx !== i && (
            <button
              type="button"
              onClick={() => removeIdx(i)}
              data-testid={`${testidRoot}-remove-${i}`}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1 -my-1 transition-opacity"
              aria-label={`Remove ${it}`}
              title="Remove"
            >
              <X size={13} weight="bold" />
            </button>
          )}
        </li>
      ))}
      {addOpen ? (
        <li className={`flex items-center gap-2 border-l-2 ${rule} pl-3 py-1.5`}>
          <Input
            autoFocus
            value={addDraft}
            onChange={(ev) => setAddDraft(ev.target.value)}
            onBlur={commitAdd}
            onKeyDown={(ev) => {
              if (ev.key === "Enter") commitAdd();
              if (ev.key === "Escape") { setAddOpen(false); setAddDraft(""); }
            }}
            placeholder={addLabel}
            data-testid={`${testidRoot}-add-input`}
            className="flex-1 rounded-none bg-transparent border-0 p-0 h-auto font-serif text-sm leading-relaxed focus-visible:ring-0"
          />
          <button
            type="button"
            onMouseDown={(ev) => ev.preventDefault()} /* keep input focused → let onBlur commit */
            onClick={commitAdd}
            data-testid={`${testidRoot}-add-commit`}
            className="text-primary hover:opacity-80 p-1 -my-1"
            aria-label={`Save ${addLabel}`}
          >
            <Check size={13} weight="bold" />
          </button>
        </li>
      ) : (
        <li>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid={`${testidRoot}-add-btn`}
            className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1 mt-1"
          >
            <Plus size={11} weight="bold" /> {addLabel}
          </button>
        </li>
      )}
    </ul>
  );
}
